/**
 * Panel 1 — Connections: which orgs may call which connectors, with what
 * credential health. Credential REFERENCES only; never any secret material.
 * Deactivation stays an API/runbook action for this rev (no half-built wizard).
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { FilterChip, SearchInput, StatusPill } from '../../components';
import { fetchOpsConnections, OpsConnection } from '../../api/ops';
import { PanelFrame, Mono, EmptyState, Loading, ErrorNote, age } from './OpsScreen';

const STATUSES = ['active', 'expired', 'revoked'] as const;

export default function ConnectionsPanel() {
  const [rows, setRows] = React.useState<OpsConnection[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string>('active');
  const [org, setOrg] = React.useState('');

  React.useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    fetchOpsConnections({ status, org: org.trim() || undefined })
      .then((r) => live && setRows(r.connections))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [status, org]);

  return (
    <PanelFrame
      title="Connections"
      subtitle="Which orgs may call which connectors, and the health of the credential behind each."
      toolbar={
        <>
          <View style={{ width: 320 }}>
            <SearchInput value={org} onChange={setOrg} placeholder="Filter by org id…" mono />
          </View>
          {STATUSES.map((s) => (
            <FilterChip key={s} label={s} active={status === s} onPress={() => setStatus(status === s ? '' : s)} />
          ))}
        </>
      }
    >
      {error ? <ErrorNote text={`Could not load connections: ${error}`} /> : null}
      {rows === null && !error ? <Loading /> : null}
      {rows !== null && rows.length === 0 ? (
        <EmptyState text="No connector grants. Grants are provisioned during operator onboarding." />
      ) : null}
      {rows !== null && rows.length > 0 ? (
        <View style={styles.table}>
          <View style={[styles.row, styles.headRow]}>
            <Text style={[styles.h, styles.cOrg]}>Org</Text>
            <Text style={[styles.h, styles.cConn]}>Connector</Text>
            <Text style={[styles.h, styles.cStatus]}>Status</Text>
            <Text style={[styles.h, styles.cSecret]}>Credential ref</Text>
            <Text style={[styles.h, styles.cRot]}>Last rotation</Text>
            <Text style={[styles.h, styles.cHealth]}>OAuth health</Text>
          </View>
          {rows.map((r) => (
            <View key={r.connection_id} style={styles.row}>
              <View style={styles.cOrg}><Mono dim>{r.org}</Mono></View>
              <View style={styles.cConn}>
                <Mono>{r.connector}</Mono>
                <Text style={styles.subtle}>{r.connector_name}</Text>
              </View>
              <View style={styles.cStatus}>
                <StatusPill
                  label={r.status}
                  tone={r.status === 'active' ? 'success' : r.status === 'expired' ? 'warn' : 'neutral'}
                />
              </View>
              {/* The reference string only — a secret never reaches this screen. */}
              <View style={styles.cSecret}><Mono dim>{r.secret_ref}</Mono></View>
              <View style={styles.cRot}>
                {r.webhook_secret_rotated_at ? (
                  <Text style={styles.cell} {...({ title: r.webhook_secret_rotated_at } as object)}>
                    {age(r.webhook_secret_rotated_at)}
                  </Text>
                ) : (
                  <Text style={styles.subtle}>never rotated</Text>
                )}
              </View>
              <View style={styles.cHealth}>
                <StatusPill
                  label={r.oauth_health === 'ok' ? 'ok' : 'refresh failing'}
                  tone={r.oauth_health === 'ok' ? 'success' : 'warn'}
                  dot={false}
                />
              </View>
            </View>
          ))}
        </View>
      ) : null}
      <Text style={styles.footnote}>
        Grants are provisioned via the onboarding runbook; deactivation is an API action for this revision.
      </Text>
    </PanelFrame>
  );
}

const styles = StyleSheet.create({
  table: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.card, overflow: 'hidden' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  },
  headRow: { backgroundColor: colors.paper2 },
  h: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink3, textTransform: 'uppercase' as any },
  cell: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2 },
  subtle: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4 },
  cOrg: { flex: 2.2 },
  cConn: { flex: 1.4 },
  cStatus: { flex: 1 },
  cSecret: { flex: 2 },
  cRot: { flex: 1.2 },
  cHealth: { flex: 1.2 },
  footnote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4 },
});
